import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { StepNav } from 'slab'
import { useGetPayrollPaySchedulesPreview as usePayrollPaySchedulesPreview } from 'utils/web_api'
import {
  type PostPayrollPaySchedulesBankClosureStrategy as BankClosureStrategy,
  PostPayrollPaySchedulesFrequency as PayFrequency,
} from 'utils/web_api.schemas'

interface PayDatePreviewProps {
  frequency: PayFrequency
  bankClosureStrategy: BankClosureStrategy
  firstDay?: number
  secondDay?: number
  referencePayDate?: string
}

function hasNecessaryData ({ frequency, bankClosureStrategy, firstDay, secondDay, referencePayDate }: PayDatePreviewProps) {
  if (!bankClosureStrategy) return false

  switch (frequency) {
    case PayFrequency.monthly:
      return !!firstDay
    case PayFrequency.semi_monthly:
      return !!firstDay && !!secondDay
    case PayFrequency.bi_weekly:
    case PayFrequency.weekly:
      return !!referencePayDate
    default:
      return false
  }
}

export function PayDatePreview ({
  frequency,
  bankClosureStrategy,
  firstDay,
  secondDay,
  referencePayDate,
}: PayDatePreviewProps) {
  const { data, error } = usePayrollPaySchedulesPreview(
    {
      frequency,
      bank_closure_strategy: bankClosureStrategy,
      first_day: firstDay,
      second_day: secondDay,
      reference_pay_date: referencePayDate,
    },
    {
      query: {
        enabled: hasNecessaryData({
          frequency,
          bankClosureStrategy,
          firstDay,
          secondDay,
          referencePayDate,
        }),
      },
    }
  )

  function renderPayDates () {
    if (error) {
      return (
        <div>Could not load pay schedule preview because of an error</div>
      )
    }

    if (!data) {
      return (
        <StepNav data-testid='pay-date-preview-skeleton'>
          <StepNav.Step active name={<SkeletonItem widthClass='w-32' />} />
          <StepNav.Step active name={<SkeletonItem widthClass='w-36' />} />
          <StepNav.Step active name={<SkeletonItem widthClass='w-28' />} />
        </StepNav>
      )
    }

    return (
      <StepNav>
        {data?.data?.map((period, index) => (
          <StepNav.Step
            key={index}
            active
            name={new Date(period.pay_date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              timeZone: 'UTC',
            })}
          />
        ))}
      </StepNav>
    )
  }

  return (
    <div className='p-4 rounded-lg border border-gray-300 min-w-64 shrink-0'>
      <p className='mb-4'>Your next pay dates are:</p>
      {renderPayDates()}
    </div>
  )
}
